import styled from '@emotion/styled';
import React from 'react';
import { TestId } from '@components/test-ids';
import { ThemeProps } from '@theme/base';
import { useI18NextContext } from '@components/hooks';
import { mq } from '@styles/breakpoints';
import { Icon } from '@components/common/generic-icon';
import { ZIndex } from '@styles/z-index';
import { boxShadowMixins } from '@styles/mixins';
import { Amount, Price } from '@model/common';
import { TripTypes } from '@model/contentful';
import { TourLink } from '@components/common/next-link/tour-link';
import { LazyLoadBackgroundImage } from '@components/common/lazy-load';
import { RippleCard } from '@components/common/ripple-card';
import { TILE_FALLBACK_IMAGE } from '@mock/fallback';

/* ***************** *
 *       Types       *
 * ***************** */
export interface TourProductTileProps {
  testId?: string;
  name?: string;
  price?: Price;
  imageUrl: string;
  path?: string;
  subTitle?: string;
  nights?: number;
  tripType?: TripTypes;
  sizes?: string;
}

/* ***************** *
 *       Styles      *
 * ***************** */

const Container: any = styled.div({
  height: 400,
  position: 'relative',
  width: '100%',

  [mq.medium]: {
    height: 500
  }
});

const TileWrapper: any = styled(RippleCard)(({ theme }: ThemeProps) => ({
  display: 'flex',
  width: '100%',
  overflow: 'hidden',
  flexDirection: 'column',
  justifyContent: 'flex-end',
  position: 'relative',
  height: '100%',
  cursor: 'pointer',
  borderRadius: theme.custom.spacing.small,
  boxShadow: boxShadowMixins(theme.custom.colors.group10.base).light,

  ['&:hover']: {
    backgroundSize: '110%'
  }
}));

const BackgroundImage: any = styled(LazyLoadBackgroundImage)(({ theme }: ThemeProps) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  transition: `transform ${theme.custom.transitions.longSmooth}`,
  zIndex: ZIndex.BASE,
  WebkitUserSelect: 'none' /* disable selection/Copy of UIWebView */,
  WebkitTouchCallout: 'none' /* disable the IOS popup when long-press on a link */,

  ['&:hover']: {
    transform: 'scale(1.2)'
  }
}));

const TourInfoCard: any = styled.div(({ theme }: ThemeProps) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  backgroundColor: theme.custom.colors.overlay.dark,
  backdropFilter: 'blur(10px)',
  minHeight: 134,
  padding: theme.custom.spacing.large,
  color: theme.custom.colors.white,
  zIndex: ZIndex.CONTENT,
  pointerEvents: 'none',
  borderBottomLeftRadius: 'inherit',
  borderBottomRightRadius: 'inherit'
}));

const TourName: any = styled.div(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.h5 as any),
  margin: 0,
  lineHeight: '2.5rem',
  fontWeight: 500
}));

const TourLength: any = styled.p(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.paragraph as any),
  margin: 0,

  ['i']: {
    fontSize: '1.4rem',
    marginRight: theme.custom.spacing.xSmall
  }
}));

const TourPrice: any = styled.p(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.labels.attribute as any),
  margin: 0
}));

const TourPriceAmount = styled.span(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.h5 as any),
  margin: 0
}));

const StyledIcon = styled(Icon)(({ theme }: ThemeProps) => ({
  color: theme.custom.colors.white
}));

/* ****************************** *
 *    TourProductTileComponent    *
 * ***************************** */

export const TourProductTile = ({
  name,
  price,
  imageUrl,
  path,
  tripType,
  testId,
  subTitle,
  nights,
  sizes
}: TourProductTileProps) => {
  const t: any = useI18NextContext();
  if (!path) return null;
  return (
    <Container data-testid={testId || TestId.tourProductTile.main}>
      <TourLink path={path} tripType={tripType} data-testid={testId || TestId.tourProductTile.main}>
        <TileWrapper>
          <BackgroundImage src={imageUrl || TILE_FALLBACK_IMAGE} sizes={sizes} />
          <TourInfoCard data-testid={TestId.tourProductTile.infoCard}>
            <TourName data-testid={TestId.tourProductTile.tourName}>{name}</TourName>
            <TourLength data-testid={TestId.tourProductTile.tourLength}>
              <StyledIcon name={'mhi-map-pin-outline'} />
              {subTitle && <span>{subTitle}</span>}
              {nights && <span>{subTitle}</span>}
            </TourLength>
            {!!price?.amount && (
              <TourPrice data-testid={TestId.tourProductTile.tourPrice}>
                {t('common__label--from')}{' '}
                <TourPriceAmount>{new Amount(price?.amount, price?.currency.code).format()}</TourPriceAmount>{' '}
                {t('common__label--per-person-short')}
              </TourPrice>
            )}
          </TourInfoCard>
        </TileWrapper>
      </TourLink>
    </Container>
  );
};
